import type { StatementRowError, UploadResult } from '@/services/reconciliation';

/**
 * B12: the upload response also reports rows it SKIPPED because the same line
 * (same policy number, amount and statement date, for this broker) was already
 * on file. Declared here while `services/reconciliation.ts` is outside this
 * workstream.
 */
export type UploadResultWithSkips = UploadResult & {
  skipped?: number;
  skipped_rows?: StatementRowError[];
  /**
   * Rows already on file whose line was still unmatched / mismatched: the
   * existing line was re-checked against today's policies and commission
   * (no duplicate created). `status` is the line's status after the re-check.
   */
  rechecked?: number;
  rechecked_rows?: (StatementRowError & { status?: string })[];
};

/** How many rows an upload skipped as already imported. */
export function uploadSkippedCount(result?: UploadResultWithSkips | null): number {
  return typeof result?.skipped === 'number' ? result.skipped : 0;
}

/** How many already-imported rows re-checked an unresolved line. */
export function uploadRecheckedCount(result?: UploadResultWithSkips | null): number {
  return typeof result?.rechecked === 'number' ? result.rechecked : 0;
}

/** 3e: how many rows corrected the line already on file for that period. */
export function uploadUpdatedCount(result?: UploadResultWithSkips | null): number {
  return typeof result?.updated === 'number' ? result.updated : 0;
}

/**
 * What the Comparison tab says after a statement upload.
 *
 * The defect this exists for: the upload endpoint answered a 100%-failed file
 * with `{reconciled: 0, disputed: 0, unmatched: 0, errors: [...]}` and the tab
 * toasted "0 reconciled, 0 disputed, 0 unmatched" — the exact same sentence a
 * successful upload of an empty file produced. `errors` was never rendered
 * anywhere. A broker whose entire statement was rejected saw a green tick.
 *
 * So the rules here are:
 *
 *  - a rejected row is ALWAYS shown, with its row number and the reason;
 *  - a partial import says both halves ("6 imported, 2 rejected"), never just
 *    the happy half;
 *  - the toast for an import with rejections is an error toast, not a success.
 */

/** Normalise one entry of the API's `errors` list. */
function toRowError(value: unknown): StatementRowError | null {
  // Older servers (and the management command) phrased a row error as a plain
  // string. Render it rather than dropping it on the floor.
  if (typeof value === 'string') {
    return value ? { row: null, policy_number: '', message: value } : null;
  }
  if (value && typeof value === 'object') {
    const row = value as Record<string, unknown>;
    if (typeof row.message === 'string' && row.message) {
      return {
        row: typeof row.row === 'number' ? row.row : null,
        policy_number: typeof row.policy_number === 'string' ? row.policy_number : '',
        message: row.message,
      };
    }
  }
  return null;
}

/**
 * Every rejected row from an upload, whether the request succeeded with some
 * rows rejected (200 body) or failed outright (400 body — an upload where
 * nothing imported is a 400, and its body carries the same `errors` list).
 */
export function uploadRowErrors(
  result?: UploadResult | null,
  error?: unknown,
): StatementRowError[] {
  const fromResult = Array.isArray(result?.errors) ? result?.errors : undefined;
  const responseData = (error as { response?: { data?: unknown } } | undefined)?.response?.data as
    | Record<string, unknown>
    | undefined;
  const fromError = Array.isArray(responseData?.errors)
    ? (responseData?.errors as unknown[])
    : undefined;

  const raw = fromResult ?? fromError ?? [];
  return raw.map(toRowError).filter((e): e is StatementRowError => e !== null);
}

/** "Row 3 — …" / "…" when the server could not say which row. */
export function describeRowError(rowError: StatementRowError): string {
  const where = rowError.row != null ? `Row ${rowError.row}` : 'This row';
  const which = rowError.policy_number ? ` (${rowError.policy_number})` : '';
  return `${where}${which} — ${rowError.message}`;
}

export interface UploadOutcome {
  /**
   * True when the upload did its job for at least one row: a row became a
   * statement line, or was recognised as already on file (B12). Re-uploading a
   * statement that is already in is not a failure — nothing was lost.
   */
  imported: boolean;
  /** Rows that became statement lines. */
  processed: number;
  /** Rows the importer refused. */
  failed: number;
  /** Rows skipped because they were already imported (B12). */
  skipped: number;
  /** Already-imported rows whose unresolved line was re-checked (B12). */
  rechecked: number;
  /** 3e: rows that corrected the amount on the line already on file. */
  updated: number;
  /** The sentence to toast. */
  message: string;
}

/**
 * Read an upload response honestly. `processed`/`failed`/`received` are recent
 * additions to the response, so both fall back to what an older body can prove.
 */
export function uploadOutcome(result: UploadResultWithSkips): UploadOutcome {
  const matched = result.reconciled ?? 0;
  const mismatched = result.disputed ?? 0;
  const noPolicy = result.unmatched ?? 0;
  const processed = result.processed ?? matched + mismatched + noPolicy;
  const failed = result.failed ?? uploadRowErrors(result).length;
  const skipped = uploadSkippedCount(result);
  const rechecked = uploadRecheckedCount(result);
  const updated = uploadUpdatedCount(result);

  // #436: a policy-level statement is stored, not matched, by the upload
  // itself, so "0 matched, 0 mismatched" would read as a verdict it is not.
  const breakdown = result.format
    ? `read as ${result.format_label ?? result.format}`
    : `${matched} matched, ${mismatched} mismatched, ${noPolicy} with no matching policy`;
  const recheckedStatuses = (result.rechecked_rows ?? []).reduce(
    (acc, row) => {
      if (row.status === 'reconciled') acc.matched += 1;
      else if (row.status === 'disputed') acc.mismatched += 1;
      else acc.noPolicy += 1;
      return acc;
    },
    { matched: 0, mismatched: 0, noPolicy: 0 },
  );
  const recheckedNote =
    rechecked > 0
      ? ` ${rechecked} already-imported row(s) were re-checked against the current policies and commission ` +
        `(now ${recheckedStatuses.matched} matched, ${recheckedStatuses.mismatched} mismatched, ` +
        `${recheckedStatuses.noPolicy} with no matching policy) — no duplicates were created.`
      : '';
  // 3e. This note is not decoration: an upload that ONLY corrects amounts
  // imports no new row, and without it the outcome below read "Nothing was
  // imported — the file had no usable rows" over a file that had just changed
  // what the insurer is recorded as having paid.
  const updatedNote =
    updated > 0
      ? ` ${updated} row(s) stated a different amount and updated the line already on file — ` +
        'the difference was recalculated and the change recorded in the line\'s history.'
      : '';
  const skippedNote =
    (skipped > 0 ? ` ${skipped} row(s) were already imported and were skipped — no duplicates were created.` : '') +
    recheckedNote +
    updatedNote;

  if (processed === 0 && (skipped > 0 || rechecked > 0 || updated > 0)) {
    return {
      imported: true,
      processed,
      failed,
      skipped,
      rechecked,
      updated,
      message:
        'No new rows.' +
        skippedNote +
        (failed > 0 ? ` ${failed} row(s) rejected — see below.` : ''),
    };
  }
  if (processed === 0) {
    return {
      imported: false,
      processed,
      failed,
      skipped,
      rechecked,
      updated,
      message:
        failed > 0
          ? `Nothing was imported — all ${failed} row(s) were rejected.`
          : 'Nothing was imported — the file had no usable rows.',
    };
  }
  if (failed > 0) {
    return {
      imported: true,
      processed,
      failed,
      skipped,
      rechecked,
      updated,
      message: `${processed} row(s) imported (${breakdown}). ${failed} row(s) rejected — see below.${skippedNote}`,
    };
  }
  return {
    imported: true,
    processed,
    failed,
    skipped,
    rechecked,
    updated,
    message: `${processed} row(s) imported: ${breakdown}.${skippedNote}`,
  };
}
